import React, { useEffect, useState } from 'react'
import { Button, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import TimeBracketInput from './TimeBracketInput'
import { DEFAULT_TIME_BRACKETS } from '../constants'
import TextInfoBox from './TextInfoBox'

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(2),
    },
    successMessage: {
        color: theme.palette.success.main,
        marginLeft: '20px'
    }
}))

export default function TimeBracketsConfig({ projectId }) {
    const classes = useStyles()

    const [project, setProject] = useState(null)
    const [currentTimeBrackets, setCurrentTimeBrackets] = useState([])
    const [formHasErrors, setFormHasErrors] = useState(false)
    const [didUpdate, setDidUpdate] = useState(false)

    useEffect(() => {
        async function getProject(projectId) {
            try {
                const headers = { Authorization: `Bearer ${localStorage.getItem('idToken')}` }
                const response = await fetch(`/service/project/${projectId}`, { headers })
                const project = await response.json()
                loadProjectData(project)
            } catch (error) {
                alert('Something went wrong while fetching the Project data. Please have a look to the console')
                console.log(error)
            }
        }

        getProject(projectId)
    }, [])

    useEffect(() => {
        validateTimeBrackets()
    }, [ currentTimeBrackets ])

    function addTimeBracket() {
        const withNewTimeBracket = [...currentTimeBrackets, 1]
        setCurrentTimeBrackets(withNewTimeBracket)
    }

    function removeTimeBracket(atIndex) {
        const removedTimeBracket = currentTimeBrackets.filter((_, index) => index !== atIndex)
        setCurrentTimeBrackets(removedTimeBracket)
    }

    function updateTimeBracket(value, atIndex) {
        const newCurrentTimeBrackets = [ ...currentTimeBrackets ]
        newCurrentTimeBrackets[atIndex] = Number(value)
        setCurrentTimeBrackets(newCurrentTimeBrackets)
    }

    function loadTimeBrackets(timeBrackets) {
        if (timeBrackets.length === 0) {
            setCurrentTimeBrackets(DEFAULT_TIME_BRACKETS)
        } else {
            setCurrentTimeBrackets(timeBrackets)
        }
    }

    function loadProjectData (project) {
        setProject(project)

        if (!project.tempo) {
            loadTimeBrackets([])
            return
        } 

        if (project.tempo.isochroneBracketInterval) {
            loadTimeBrackets(project.tempo.isochroneBracketInterval)
        }
    }

    function validateTimeBracketValue (value) {
        if (!Number(value) || Number(value) > 3600 || Number(value) < 60) {
            return { isValid: false, errorMessage: "Has to be between 1 and 60"}
        }

        const isDuplicated = (currentTimeBrackets.filter(x => x === Number(value))).length > 1

        if (isDuplicated) {
            return { isValid: false, errorMessage: "Value already exists" }
        }

        const currentIndex = currentTimeBrackets.indexOf(value)
        let invalidOrderError = ''
        currentTimeBrackets.forEach((isochroneValue, isochroneIndex) => {
            if(isochroneIndex > currentIndex && isochroneValue > value){
                invalidOrderError = `Value is less than isochrone ${isochroneIndex + 1}. Isochrones must be in descending order`
            }else if(isochroneIndex < currentIndex && isochroneValue < value){
                invalidOrderError = `Value is greater than isochrone ${isochroneIndex + 1}. Isochrones must be in descending order`;
            }
        })
        if(invalidOrderError){
            return { isValid: false, errorMessage: invalidOrderError }
        }

        return { isValid: true, errorMessage: '' }
    }

    function validateTimeBrackets() {
        const validationResults = currentTimeBrackets.map(timeBracket => {
            const { isValid, errorMessage } = validateTimeBracketValue(timeBracket)
            return { isValid, errorMessage }
        })

        const areAllValid = (validationResults.filter(validation => !validation.isValid)).length === 0

        if (areAllValid) {
            setFormHasErrors(false)
        } else {
            setFormHasErrors(true)
        }
    }



    async function saveTimeBrackets() {
        const url = `/service/project/${projectId}/tempoTimeBrackets`
        const method = 'POST'
        const headers = { Authorization: `Bearer ${localStorage.getItem('idToken')}`, 'Content-Type': 'application/json' }
        const body = JSON.stringify({ timeBrackets: currentTimeBrackets })
        const res = await fetch(url, { method, headers, body })

        if (res.status !== 204) {
            alert('Something went wrong updating the time brackets. Please have a look to the console')
        }
        
        setDidUpdate(true)
    }

    function renderInputs() {
        if (currentTimeBrackets.length === 1) {
            return (
                <TimeBracketInput
                    timeBrackets={currentTimeBrackets}
                    value={currentTimeBrackets[0]}
                    number={1}
                    key={`${currentTimeBrackets[0]}-${1}`}
                    hideDeleteButton={true}
                    updateTimeBracket={updateTimeBracket}
                    validateValue={validateTimeBracketValue}
                />
            )
        }

        return currentTimeBrackets.map((timeBracket, index) => (
            <TimeBracketInput
                timeBrackets={currentTimeBrackets}
                value={timeBracket}
                number={index + 1}
                key={`${timeBracket}-${index}`}
                removeInput={removeTimeBracket}
                updateTimeBracket={updateTimeBracket}
                validateValue={validateTimeBracketValue}
            />
        ))
    }

    return (
        <>
            <label className="box-label">Tempo Configuration</label>
            <TextInfoBox />
            <Paper className={classes.container}>
                <Typography variant="h5" >{project && project.name}</Typography>
                <hr />
                <Typography variant="body1">Set the isochrone time brackets in minutes</Typography>
                <br />
                {
                    project && renderInputs()
                }
                <br />
                <Button color="primary" onClick={addTimeBracket}>
                    <Typography variant="body2">+ Add isochrone time bracket</Typography>
                </Button>
                <hr />
                <div style={{ display: "flex", alignItems: 'center' }}>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={saveTimeBrackets}
                        disabled={formHasErrors}
                    >
                        Save Changes
                    </Button>
                    { didUpdate && <Typography className={classes.successMessage}>Time brackets were successfully updated!</Typography> }
                </div>
             
            </Paper>
        </>
    )
}